import { useEffect, useState } from 'react';
import { FormItem } from '../../../types/Survey';
import { ManagedResultsLookup } from '../../../helpers/surveyPredicates';
import { ResultModel, StepResultModel } from '../../../types/SurveyResult';
import { buildStepResult, initializeFormItemResults } from './surveyStepUtils';

/**
 * Manages the results state for steps that contain form items (FormStep,
 * InstructionStep). Initialises results from managedResults on first render,
 * propagates them on mount so downstream predicates evaluate immediately, and
 * provides a handleResultChange callback that keeps both local state and the
 * parent's managed results in sync.
 */
export function useFormItemResults(
    stepIdentifier: string,
    formItems: FormItem[],
    managedResults: ManagedResultsLookup,
    visibleFormItems: FormItem[],
    presentedDate: string,
    onResultChange: (stepResult: StepResultModel) => void
) {
    const [results, setResults] = useState<ResultModel[]>(() =>
        initializeFormItemResults(formItems, managedResults, stepIdentifier, presentedDate)
    );

    const visibleResults = results.filter(r =>
        visibleFormItems.some(item => item.identifier === r.Identifier)
    );

    // Propagate initial default/restored results on mount so downstream predicates
    // on other steps can evaluate correctly immediately.
    useEffect(() => {
        onResultChange(buildStepResult(stepIdentifier, presentedDate, visibleResults));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleResultChange(newResult: ResultModel) {
        const newResults = results.map(r =>
            r.Identifier === newResult.Identifier ? newResult : r
        );
        setResults(newResults);
        const updatedVisible = newResults.filter(r =>
            visibleFormItems.some(item => item.identifier === r.Identifier)
        );
        onResultChange(buildStepResult(stepIdentifier, presentedDate, updatedVisible));
    }

    return { results, visibleResults, handleResultChange };
}
